import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import {
  EquipePageHero,
  EquipeRoleSection,
  STAFF_JOIN_STEP_ICONS,
  STAFF_ROLE_ORDER,
  groupStaffByRole,
} from '@/components/help-center/equipe-shared';
import {
  Sparkles,
  BookOpen,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useCms } from '@/components/painel/cms/cms-context';
import { useStaffOnlineStatuses } from '@/hooks/use-staff-online-statuses';
import { cn } from '@/lib/utils';
import { getPowerLoreTheme } from '@/lib/power-lore';
import { useHelpAppearance } from '@/lib/help-appearance';
import { PublicPageSkeleton } from '@/components/help-center/PublicPageSkeleton';

const lore = getPowerLoreTheme('normal');

function FilledMoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.75 15.07A9.75 9.75 0 0 1 8.93 2.25a.75.75 0 0 0-.9-.9A11.25 11.25 0 1 0 22.65 15.97a.75.75 0 0 0-.9-.9Z" />
    </svg>
  );
}

function FilledSunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="4.25" />
      <path d="M12 1.75a.75.75 0 0 1 .75.75v1.75a.75.75 0 0 1-1.5 0V2.5A.75.75 0 0 1 12 1.75Zm0 17.5a.75.75 0 0 1 .75.75V21.75a.75.75 0 0 1-1.5 0v-1.75a.75.75 0 0 1 .75-.75ZM22.25 12a.75.75 0 0 1-.75.75h-1.75a.75.75 0 0 1 0-1.5H21.5a.75.75 0 0 1 .75.75ZM4.25 12a.75.75 0 0 1-.75.75H1.75a.75.75 0 0 1 0-1.5H3.5A.75.75 0 0 1 4.25 12Zm15.01-7.26a.75.75 0 0 1 0 1.06l-1.24 1.24a.75.75 0 1 1-1.06-1.06l1.24-1.24a.75.75 0 0 1 1.06 0ZM7.04 16.96a.75.75 0 0 1 0 1.06l-1.24 1.24a.75.75 0 0 1-1.06-1.06l1.24-1.24a.75.75 0 0 1 1.06 0Zm11.22 1.06a.75.75 0 0 1-1.06 0l-1.24-1.24a.75.75 0 1 1 1.06-1.06l1.24 1.24a.75.75 0 0 1 0 1.06ZM7.04 7.04a.75.75 0 0 1-1.06 0L4.74 5.8A.75.75 0 0 1 5.8 4.74l1.24 1.24a.75.75 0 0 1 0 1.06Z" />
    </svg>
  );
}

export default function EquipePage() {
  const { site, siteReady } = useCms();
  const grouped = useMemo(() => groupStaffByRole(site.staffMembers), [site.staffMembers]);
  const staffXatIds = useMemo(
    () => site.staffMembers.map((m) => String(m.xatId ?? '').trim()).filter(Boolean),
    [site.staffMembers]
  );
  const { byId: onlineById } = useStaffOnlineStatuses(staffXatIds);
  const { appearance, setMode, isLight } = useHelpAppearance();
  const join = site.staffJoinSection;
  const learnMore = site.staffLearnMore;

  if (!siteReady) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Navbar />
        <main className="flex-1 bg-[#030023]">
          <PublicPageSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  const appearanceToggle = (
    <div
      className="inline-flex shrink-0 items-center rounded-full border border-sky-400/25 bg-white/[0.06] p-0.5 backdrop-blur-sm"
      role="group"
      aria-label="Alternar aparência do conteúdo"
    >
      <button
        type="button"
        onClick={() => setMode('dark')}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
          appearance === 'dark'
            ? 'bg-sky-500/25 text-sky-100'
            : 'text-sky-300/75 hover:bg-sky-400/10 hover:text-sky-100'
        )}
        aria-pressed={appearance === 'dark'}
        aria-label="Modo escuro"
        title="Modo escuro"
      >
        <FilledMoonIcon className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setMode('light')}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
          appearance === 'light'
            ? 'bg-sky-500/25 text-sky-100'
            : 'text-sky-300/75 hover:bg-sky-400/10 hover:text-sky-100'
        )}
        aria-pressed={appearance === 'light'}
        aria-label="Modo claro"
        title="Modo claro"
      >
        <FilledSunIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  return (
    <TooltipProvider delayDuration={250}>
      <div className={cn('flex min-h-screen flex-col', isLight ? 'bg-slate-50' : 'bg-white')}>
        <Navbar />
        <main
          className={cn(
            'flex-1 transition-colors duration-300',
            isLight ? 'bg-[#F4F7FC]' : 'bg-[#030023]'
          )}
        >
          <EquipePageHero
            title={site.staffPageTitle}
            subtitle={site.staffPageSubtitle}
            appearanceToggle={appearanceToggle}
            isLight={isLight}
          />

          <section className="relative px-6 pb-16 pt-2 sm:pb-20 sm:pt-4 md:pb-24">
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b to-transparent',
                isLight ? 'from-[#F4F7FC]' : 'from-[#030023]'
              )}
              aria-hidden
            />
            <div className="relative mx-auto max-w-7xl space-y-16 sm:space-y-20 md:space-y-24">
              {STAFF_ROLE_ORDER.map((role) => (
                <EquipeRoleSection
                  key={role}
                  role={role}
                  members={grouped[role]}
                  roleDescriptionText={site.staffRoleDescriptions[role]}
                  isLight={isLight}
                  onlineById={onlineById}
                />
              ))}
            </div>
          </section>

          <section
            className={cn(
              'relative overflow-hidden px-6 py-16 sm:py-20 md:py-24',
              isLight ? 'border-t border-[rgba(0,11,106,0.14)]' : 'border-t border-sky-500/20'
            )}
          >
            <div
              className={cn(
                'pointer-events-none absolute -left-24 top-20 h-64 w-64 rounded-full blur-3xl',
                isLight ? 'bg-[rgba(0,11,106,0.1)]' : lore.orbPrimary
              )}
              aria-hidden
            />
            <div
              className={cn(
                'pointer-events-none absolute -right-20 bottom-10 h-56 w-56 rounded-full blur-3xl',
                isLight ? 'bg-[rgba(0,11,106,0.08)]' : lore.orbSecondary
              )}
              aria-hidden
            />

            <div className="relative mx-auto max-w-7xl">
              <header className="mb-10 max-w-3xl sm:mb-12">
                <h2
                  className={cn(
                    'font-poppins text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl',
                    isLight ? 'text-slate-900' : 'text-white'
                  )}
                >
                  {join.title}
                </h2>
                <p
                  className={cn(
                    'mt-4 max-w-2xl font-inter text-sm leading-relaxed sm:text-[15px] md:text-base',
                    isLight ? 'text-slate-600' : 'text-[#AEB7D6]'
                  )}
                >
                  {join.subtitle}
                </p>
              </header>

              <div
                className={cn(
                  'relative overflow-hidden rounded-2xl border backdrop-blur-sm px-5 py-7 sm:px-8 sm:py-9',
                  isLight
                    ? 'border-[rgba(0,11,106,0.14)] bg-white shadow-[0_12px_40px_-24px_rgba(0,11,106,0.22)]'
                    : cn('border-sky-500/20 bg-gradient-to-br from-[#030023] via-[#071238] to-[#000b6a]/85', lore.cardShadow)
                )}
              >
                <div
                  className={cn(
                    'pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent',
                    isLight ? 'via-[rgba(0,11,106,0.22)]' : 'via-sky-400/30'
                  )}
                  aria-hidden
                />

                <div className="relative">
                  <div
                    className={cn(
                      'pointer-events-none absolute bottom-2 left-[11px] top-2 w-px -translate-x-1/2 sm:left-[15px]',
                      isLight
                        ? 'bg-gradient-to-b from-[rgba(0,11,106,0.55)] via-[rgba(0,11,106,0.22)] to-[rgba(0,11,106,0.05)]'
                        : 'bg-gradient-to-b from-sky-400/60 via-sky-400/25 to-sky-400/5'
                    )}
                    aria-hidden
                  />

                  <ol className="relative m-0 list-none space-y-0 p-0">
                    {join.steps.map((step, i) => {
                      const Icon: LucideIcon = STAFF_JOIN_STEP_ICONS[step.icon] ?? Sparkles;
                      const number = String(i + 1).padStart(2, '0');
                      const isLast = i === join.steps.length - 1;
                      return (
                        <li
                          key={step.id}
                          className={cn('relative pl-10 sm:pl-12', !isLast && 'pb-6 sm:pb-8')}
                        >
                          <span
                            className="absolute left-[11px] top-4 z-10 flex h-3.5 w-3.5 -translate-x-1/2 items-center justify-center sm:left-[15px] sm:top-5"
                            aria-hidden
                          >
                            <span
                              className={cn(
                                'absolute h-3.5 w-3.5 rounded-full',
                                isLight
                                  ? 'bg-[rgba(0,11,106,0.85)] shadow-[0_0_12px_2px_rgba(0,11,106,0.35)]'
                                  : 'bg-sky-400/90 shadow-[0_0_12px_2px_rgba(56,189,248,0.55)]'
                              )}
                            />
                            <span
                              className={cn(
                                'absolute h-7 w-7 animate-pulse rounded-full',
                                isLight ? 'bg-[rgba(0,11,106,0.12)]' : 'bg-sky-400/20'
                              )}
                            />
                          </span>

                          <article
                            className={cn(
                              'group rounded-2xl border border-transparent bg-transparent p-3.5 sm:p-4',
                              'transition-all duration-200',
                              isLight
                                ? 'hover:-translate-y-0.5 hover:border-[rgba(0,11,106,0.22)] hover:bg-[rgba(0,11,106,0.03)] hover:shadow-[0_0_28px_-12px_rgba(0,11,106,0.28)]'
                                : 'hover:-translate-y-0.5 hover:border-sky-400/30 hover:bg-sky-400/[0.07] hover:shadow-[0_0_28px_-12px_rgba(56,189,248,0.45)]'
                            )}
                          >
                            <div className="mb-2 flex flex-wrap items-center gap-2.5">
                              <span
                                className={cn(
                                  'font-poppins text-[11px] font-bold uppercase tracking-[0.16em]',
                                  isLight ? 'text-[#000b6a]' : 'text-sky-400/80'
                                )}
                              >
                                Passo {number}
                              </span>
                              <span
                                className={cn(
                                  'inline-flex h-7 w-7 items-center justify-center rounded-lg border transition-colors duration-200',
                                  isLight
                                    ? 'border-[rgba(0,11,106,0.2)] bg-[rgba(0,11,106,0.06)] text-[#000b6a] group-hover:border-[rgba(0,11,106,0.32)] group-hover:bg-[rgba(0,11,106,0.1)]'
                                    : 'border-sky-400/25 bg-sky-400/10 text-sky-300 group-hover:border-sky-400/45 group-hover:bg-sky-400/15'
                                )}
                              >
                                <Icon className="h-3.5 w-3.5" aria-hidden />
                              </span>
                            </div>
                            <h3
                              className={cn(
                                'font-poppins text-base font-semibold sm:text-lg',
                                isLight ? 'text-slate-900' : 'text-white'
                              )}
                            >
                              {step.title}
                            </h3>
                            <p
                              className={cn(
                                'mt-2 font-inter text-sm leading-relaxed sm:text-[15px]',
                                isLight ? 'text-slate-600' : 'text-[#AEB7D6]'
                              )}
                            >
                              {step.text}
                            </p>
                          </article>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </div>

              <div className="mt-12 sm:mt-14">
                <div
                  className="mb-8 flex items-center justify-center gap-3 sm:mb-10"
                  aria-hidden
                >
                  <span
                    className={cn(
                      'h-px w-12 bg-gradient-to-r from-transparent sm:w-20',
                      isLight ? 'to-[rgba(0,11,106,0.28)]' : 'to-sky-400/30'
                    )}
                  />
                  <span className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'h-1 w-1 rounded-full',
                        isLight ? 'bg-[rgba(0,11,106,0.4)]' : 'bg-sky-400/40'
                      )}
                    />
                    <span
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        isLight
                          ? 'bg-[#000b6a] shadow-[0_0_8px_rgba(0,11,106,0.4)]'
                          : 'bg-sky-400/80 shadow-[0_0_10px_rgba(56,189,248,0.55)]'
                      )}
                    />
                    <span
                      className={cn(
                        'h-1 w-1 rounded-full',
                        isLight ? 'bg-[rgba(0,11,106,0.4)]' : 'bg-sky-400/40'
                      )}
                    />
                  </span>
                  <span
                    className={cn(
                      'h-px w-12 bg-gradient-to-l from-transparent sm:w-20',
                      isLight ? 'to-[rgba(0,11,106,0.28)]' : 'to-sky-400/30'
                    )}
                  />
                </div>
                <div
                  className={cn(
                    'relative overflow-hidden rounded-2xl border backdrop-blur-sm px-5 py-6 sm:px-7 sm:py-7',
                    isLight
                      ? 'border-[rgba(0,11,106,0.14)] bg-gradient-to-br from-white via-[rgba(0,11,106,0.03)] to-white shadow-[0_12px_40px_-24px_rgba(0,11,106,0.22)]'
                      : cn(
                          'border-sky-400/25 bg-gradient-to-br from-[#071238]/90 via-[#030023]/85 to-[#000b6a]/80',
                          lore.cardShadow
                        )
                  )}
                >
                  <div
                    className={cn(
                      'pointer-events-none absolute -right-10 top-0 h-32 w-32 rounded-full blur-3xl',
                      isLight ? 'bg-[rgba(0,11,106,0.12)]' : 'bg-sky-400/15'
                    )}
                    aria-hidden
                  />
                  <div
                    className={cn(
                      'pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent',
                      isLight ? 'via-[rgba(0,11,106,0.28)]' : 'via-sky-400/40'
                    )}
                    aria-hidden
                  />
                  <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
                    <div className="max-w-xl">
                      <h3
                        className={cn(
                          'font-poppins text-lg font-semibold sm:text-xl',
                          isLight ? 'text-slate-900' : 'text-white'
                        )}
                      >
                        {learnMore.title}
                      </h3>
                      <p
                        className={cn(
                          'mt-1.5 font-inter text-sm leading-relaxed',
                          isLight ? 'text-slate-600' : 'text-[#AEB7D6]'
                        )}
                      >
                        {learnMore.description}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2.5">
                      <Link
                        to="/regras"
                        className={cn(
                          'inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 font-poppins text-sm font-semibold',
                          'transition-[background-color,border-color,transform] duration-200 hover:-translate-y-px',
                          isLight
                            ? 'border-[rgba(0,11,106,0.35)] bg-[#000b6a] text-white shadow-[0_0_24px_-10px_rgba(0,11,106,0.45)] hover:bg-[rgba(0,11,106,0.9)]'
                            : 'border-sky-400/40 bg-sky-500/20 text-sky-50 shadow-[0_0_24px_-10px_rgba(56,189,248,0.5)] hover:border-sky-400/55 hover:bg-sky-500/30'
                        )}
                      >
                        <BookOpen className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                        {learnMore.regrasButtonLabel}
                      </Link>
                      <Link
                        to="/conduta"
                        className={cn(
                          'inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 font-poppins text-sm font-semibold',
                          'transition-[background-color,border-color,color] duration-200',
                          isLight
                            ? 'border-[rgba(0,11,106,0.22)] bg-white text-[#000b6a] hover:border-[rgba(0,11,106,0.35)] hover:bg-[rgba(0,11,106,0.04)]'
                            : 'border-sky-500/25 bg-transparent text-sky-200 hover:border-sky-400/40 hover:bg-sky-400/10 hover:text-sky-50'
                        )}
                      >
                        <ShieldCheck className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                        {learnMore.condutaButtonLabel}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </TooltipProvider>
  );
}
